import { useQueryClient } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import c from 'utils/constants';
import { useProvider } from 'utils/providers/Provider';

import useStorage, { STORAGE_TYPE } from './useStorage';

export interface PAYLOAD {
  profile?: any; // TODO: create a PROFILE INTERFACE
  remember?: boolean;
}

function useProfile() {
  const queryClient = useQueryClient();
  const { dispatch } = useProvider();
  const { getItem, setItem, removeItem } = useStorage();

  const updateProfile = (payload: PAYLOAD) => {
    const {
      _token,
      _isEmailAddressVerified,
      _isOnboarded,
      _onboardingSteps,
      _isSetup,
      _setupSteps,
      ...rest
    } = payload.profile;

    if (_token) Cookies.set('_token', _token);

    if (_isEmailAddressVerified) {
      Cookies.set('_isEmailAddressVerified', _isEmailAddressVerified);
    }

    if (_isOnboarded) Cookies.set('_isOnboarded', _isOnboarded);

    if (_onboardingSteps)
      Cookies.set('_onboardingSteps', JSON.stringify(_onboardingSteps));

    if (_isSetup) Cookies.set('_isSetup', _isSetup);

    if (_setupSteps) Cookies.set('_setupSteps', JSON.stringify(_setupSteps));

    let STORAGE: STORAGE_TYPE = STORAGE_TYPE.local;
    let PROFILE = getItem(
      `${import.meta.env.VITE_APP_PREFIX}_profile`,
      STORAGE,
    );

    if (!PROFILE) {
      STORAGE = STORAGE_TYPE.session;
      PROFILE = getItem(`${import.meta.env.VITE_APP_PREFIX}_profile`, STORAGE);
    }

    if (PROFILE) {
      PROFILE = JSON.parse(PROFILE);
    }

    setItem(
      `${import.meta.env.VITE_APP_PREFIX}_profile`,
      JSON.stringify({ ...PROFILE, ...rest }),
      STORAGE,
    );
  };

  const removeProfile = () => {
    dispatch?.({ type: 'toggleAuthenticated', payload: false });
    dispatch?.({ type: 'toggleEmailAddressVerified', payload: false });

    removeItem(
      `${import.meta.env.VITE_APP_PREFIX}_profile`,
      STORAGE_TYPE.session,
    );

    removeItem(
      `${import.meta.env.VITE_APP_PREFIX}_profile`,
      STORAGE_TYPE.local,
    );

    removeItem(`${import.meta.env.VITE_APP_PREFIX}_misc`, STORAGE_TYPE.local);

    Cookies.remove('_isEmailAddressVerified');
    Cookies.remove('_isOnboarded');
    Cookies.remove('_role');
    Cookies.remove('_token');
    Cookies.remove('_sessionId');
    Cookies.remove('_onboardingSteps');
    Cookies.remove('_isSetup');
    Cookies.remove('_setupSteps');
    Cookies.remove('_isSetupSkipped');

    setTimeout(() => queryClient.clear());
  };

  const setProfile = (payload: PAYLOAD) => {
    const storage = payload?.remember
      ? STORAGE_TYPE.local
      : STORAGE_TYPE.session;

    const {
      _token,
      _isEmailAddressVerified,
      _isOnboarded,
      _onboardingSteps,
      _isSetup,
      _setupSteps,
      _sessionId,
      role,
      loginValidated,
    } = payload.profile;

    Cookies.set('_isEmailAddressVerified', _isEmailAddressVerified);
    Cookies.set('_isOnboarded', _isOnboarded);
    Cookies.set('_isSetup', _isSetup);

    if (_onboardingSteps)
      Cookies.set('_onboardingSteps', JSON.stringify(_onboardingSteps));

    if (_setupSteps) Cookies.set('_setupSteps', JSON.stringify(_setupSteps));

    Cookies.set('_token', _token);
    Cookies.set('_role', role);
    Cookies.set('_sessionId', _sessionId);

    if (loginValidated || c.ROLES.admin === role) {
      setItem(
        `${import.meta.env.VITE_APP_PREFIX}_profile`,
        JSON.stringify({ role }),
        storage,
      );

      if (storage === STORAGE_TYPE.local)
        setItem(
          `${import.meta.env.VITE_APP_PREFIX}_misc`,
          btoa(JSON.stringify(payload)),
          storage,
        );

      dispatch?.({ type: 'toggleAuthenticated', payload: true });
    }
  };

  return { updateProfile, removeProfile, setProfile };
}

export default useProfile;
